import { getOddIconSrc } from '@backoffice-frontend/overwatch-map-common'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { MenuIcon, TrashIcon } from '@moia-dev/pace-icons'
import { IconButton, Input, Text } from '@moia-dev/pace-web'
import {
  type ClipboardEvent,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import {
  ellipseOrbit,
  parseCoordinates,
  ROUTE_VIOLATION_LABELS,
} from '../utils'
import type { RouteViolation, Waypoint } from './types'

type WaypointListProps = {
  waypoints: Waypoint[]
  onChange: (waypoints: Waypoint[]) => void
  onClearHeading?: (waypointId: string) => void
  onReorderStart?: () => void
  violations?: RouteViolation[]
  headings?: Record<string, number>
}

export const createWaypoint = (): Waypoint => ({
  id: crypto.randomUUID(),
  lat: null,
  lng: null,
  address: '',
  validated: false,
  oddElementType: null,
  desiredLat: null,
  desiredLng: null,
  heading: null,
})

// Restrict drag movement to the Y axis only — prevents horizontal drift
// when dragging waypoints inside the floating panel.
const restrictToVerticalAxis: import('@dnd-kit/core').Modifier = ({
  transform,
}) => ({ ...transform, x: 0 })

const UNVALIDATED_COLOR = '#E87722'
const VALIDATED_COLOR = '#0532ff'
const ERROR_COLOR = '#D32F2F'

const INPUT_HEIGHT = 40
// Every item has this bottom padding so all slots are identical height —
// dnd-kit measures them uniformly and never applies scaleY compression.
const ROW_GAP = 26

// ── WaypointRow ────────────────────────────────────────────────────────────

type WaypointRowProps = {
  waypoint: Waypoint
  index: number
  showConnector: boolean
  nextLegHasViolation?: boolean
  /** True while any waypoint is being dragged — violation banners collapse, so
   * the row owns the connector even when nextLegHasViolation. */
  dragging?: boolean
  canRemove: boolean
  heading?: number
  onUpdate: (index: number, value: string) => void
  onRemove: (index: number) => void
  onClear: (index: number) => void
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void
  // biome-ignore lint/suspicious/noExplicitAny: dnd-kit spreads untyped attribute/listener maps
  dragHandleProps?: Record<string, any>
  /** Rendered inside DragOverlay — omits bottom padding and connector */
  isOverlay?: boolean
}

const WaypointRow = ({
  waypoint,
  index,
  showConnector,
  nextLegHasViolation = false,
  dragging = false,
  canRemove,
  heading,
  onUpdate,
  onRemove,
  onClear,
  onPaste,
  dragHandleProps,
  isOverlay = false,
}: WaypointRowProps) => {
  const markerColor = waypoint.validated ? VALIDATED_COLOR : UNVALIDATED_COLOR

  const hasOdd = !!waypoint.oddElementType

  return (
    <div css={{ display: 'flex', width: '100%' }}>
      {/* Left rail: badge + optional dashed connector */}
      <div
        css={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 44,
        }}
      >
        <div
          css={{
            height: INPUT_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            overflow: 'visible',
          }}
        >
          {hasOdd ? (
            <div
              css={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                zIndex: 1,
                borderRadius: 18,
                padding: '0 5px 0 3px',
                gap: 5,
                height: 32,
                backgroundColor: markerColor,
                border: '2px solid white',
                outline: '1px solid rgba(0,0,0,0.25)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                overflow: 'visible',
              }}
            >
              <img
                src={getOddIconSrc(waypoint.oddElementType!)}
                width={22}
                height={22}
                alt=""
                css={{ display: 'block', flexShrink: 0 }}
              />
              <span
                css={{
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {index + 1}
              </span>
              {heading !== undefined &&
                (() => {
                  const pillRx =
                    33 + Math.max(0, String(index + 1).length - 1) * 4
                  const { dx, dy } = ellipseOrbit(pillRx, 25, heading)
                  return (
                    <svg
                      width={14}
                      height={10}
                      viewBox="0 0 14 10"
                      fill="none"
                      css={{
                        position: 'absolute',
                        left: `calc(50% + ${dx - 7}px)`,
                        top: `calc(50% + ${dy - 5}px)`,
                        pointerEvents: 'none',
                        zIndex: 3,
                        transform: `rotate(${heading}deg)`,
                      }}
                    >
                      <path
                        d="M7 1L13 9H1Z"
                        fill="white"
                        stroke={markerColor}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                })()}
            </div>
          ) : (
            <div
              css={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1,
                borderRadius: '50%',
                width: 32,
                height: 32,
                backgroundColor: markerColor,
                border: '2px solid white',
                outline: '1px solid rgba(0,0,0,0.25)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                overflow: 'visible',
              }}
            >
              <span
                css={{
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {index + 1}
              </span>
              {heading !== undefined &&
                (() => {
                  const { dx, dy } = ellipseOrbit(24, 24, heading)
                  return (
                    <svg
                      width={14}
                      height={10}
                      viewBox="0 0 14 10"
                      fill="none"
                      css={{
                        position: 'absolute',
                        left: `calc(50% + ${dx - 7}px)`,
                        top: `calc(50% + ${dy - 5}px)`,
                        pointerEvents: 'none',
                        zIndex: 3,
                        transform: `rotate(${heading}deg)`,
                      }}
                    >
                      <path
                        d="M7 1L13 9H1Z"
                        fill="white"
                        stroke={markerColor}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                })()}
            </div>
          )}
        </div>

        {/* Connector / spacer — when the next row is a ViolationBanner it owns
            the top connector so we emit nothing here; otherwise render a fixed-
            height spacer with an optional dashed line. While dragging, violation
            banners collapse, so the row owns the connector even then. */}
        {!isOverlay && (!nextLegHasViolation || dragging) && (
          <div
            css={{
              position: 'relative',
              minHeight: ROW_GAP,
              width: '100%',
            }}
          >
            {showConnector && (
              <div
                css={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 2,
                  background: `repeating-linear-gradient(to bottom, #c4c4c4 0px, #c4c4c4 5px, transparent 5px, transparent 10px)`,
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* Input row */}
      <div
        css={{
          flex: 1,
          minWidth: 0,
          paddingLeft: 20,
          paddingBottom: isOverlay || nextLegHasViolation ? 0 : ROW_GAP,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            height: INPUT_HEIGHT,
          }}
        >
          <Input
            size="compact"
            aria-label={`Waypoint ${index + 1}`}
            placeholder="Click on map or enter coordinates..."
            value={waypoint.address}
            onChange={e => onUpdate(index, e.target.value)}
            onPaste={onPaste}
            trailing={
              <IconButton
                aria-label={`Remove waypoint ${index + 1}`}
                size="compact"
                variant="plain"
                onClick={
                  isOverlay
                    ? undefined
                    : () => (canRemove ? onRemove(index) : onClear(index))
                }
                icon={<TrashIcon />}
              />
            }
            css={{
              flex: 1,
              height: INPUT_HEIGHT,
              '& input::placeholder': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& input': { textOverflow: 'ellipsis' },
              '& [class*="_trailing"]': {
                display: 'flex',
                alignItems: 'center',
                maxHeight: 'none',
              },
              '& [class*="_trailing"] button': {
                marginRight: -6,
                transition: 'transform 150ms ease',
                '&:hover': {
                  backgroundColor: 'transparent !important',
                  transform: 'scale(1.12)',
                },
                '&:active': {
                  backgroundColor: 'transparent !important',
                  transform: 'scale(1.05)',
                },
                '&:focus': { backgroundColor: 'transparent !important' },
                '&::before, &::after': { display: 'none' },
              },
            }}
          />
          <div
            {...dragHandleProps}
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              padding: '0 2px',
              height: INPUT_HEIGHT,
              color: theme.semantic.ColorContentReduced,
              flexShrink: 0,
              cursor: dragHandleProps ? 'grab' : 'default',
              touchAction: 'none',
              '&:active': { cursor: dragHandleProps ? 'grabbing' : 'default' },
            })}
          >
            <MenuIcon css={{ width: 16, height: 16 }} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── SortableWaypoint ───────────────────────────────────────────────────────

type SortableWaypointProps = Omit<
  WaypointRowProps,
  'dragHandleProps' | 'isOverlay'
> & {
  children?: ReactNode
}

const SortableWaypoint = ({ children, ...props }: SortableWaypointProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.waypoint.id })

  // Use native style (not Emotion css) for dnd-kit transform/transition —
  // matches the backoffice reference pattern (SortableStopItem) and avoids
  // Emotion class regeneration interfering with CSS transitions.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Invisible placeholder while the DragOverlay clone is active
    opacity: isDragging ? 0 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <WaypointRow
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
      {children}
    </div>
  )
}

// ── AnimatedCollapse ───────────────────────────────────────────────────────
// Animates height from 0 → scrollHeight on mount, and collapses back when
// `collapsed` becomes true. Uses a ref to measure real content height so the
// transition is never instant even when grid/max-height tricks fail.
//
// When `unmountAfterCollapse` is true the children stay mounted until the
// collapse transition finishes, then the wrapper renders nothing — enabling
// exit animations for conditionally-rendered content.

const COLLAPSE_MS = 200

// All animation is driven imperatively via useLayoutEffect (fires before paint),
// so transitions always start from the correct "from" value regardless of React
// render batching or dnd-kit applying transforms in the same frame.
// React never writes height or opacity into the style prop, preventing re-renders
// from clobbering in-progress transitions.
// No internal unmount/remount — ViolationSlot's ref keeps content visible during
// exit, so AnimatedCollapse only needs to animate height and opacity.
const AnimatedCollapse = ({
  collapsed,
  children,
}: {
  collapsed: boolean
  children: ReactNode
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useLayoutEffect(() => {
    const w = wrapperRef.current
    const i = innerRef.current
    if (!w || !i) return

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (collapsed) {
      // Pin current rendered height before transitioning to 0.
      // offsetHeight triggers a synchronous layout — returns real height even
      // when style is 'auto', and this runs before paint so it's always correct.
      w.style.height = `${w.offsetHeight}px`
      i.style.opacity = '1'
      w.getBoundingClientRect() // commit "from" state
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        w.style.height = '0px'
        i.style.opacity = '0'
      })
    } else {
      w.style.height = '0px'
      i.style.opacity = '0'
      w.getBoundingClientRect() // commit "from" state
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        const inner = innerRef.current
        const wrapper = wrapperRef.current
        if (!inner || !wrapper) return
        wrapper.style.height = `${inner.scrollHeight}px`
        inner.style.opacity = '1'
        // Release fixed height after transition so layout can reflow freely.
        timerRef.current = setTimeout(() => {
          timerRef.current = null
          if (wrapperRef.current) wrapperRef.current.style.height = 'auto'
        }, COLLAPSE_MS)
      })
    }

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [collapsed])

  return (
    <div
      ref={wrapperRef}
      style={{ overflow: 'hidden', transition: `height ${COLLAPSE_MS}ms ease` }}
    >
      <div
        ref={innerRef}
        style={{ transition: `opacity ${COLLAPSE_MS}ms ease` }}
      >
        {children}
      </div>
    </div>
  )
}

// ── ViolationBanner ────────────────────────────────────────────────────────
// Mirrors ADMT's error-structure row: dashed red connector lines top/bottom,
// circular error icon in the left rail (44px), message box aligned to input column.

// Drag handle column width — must match WaypointRow's drag icon area so the
// message box right edge aligns with the Input right edge, not the drag icon.
const DRAG_HANDLE_WIDTH = 24

const ViolationBanner = ({
  violations,
  hasNextWaypoint = true,
}: {
  violations: RouteViolation[]
  hasNextWaypoint?: boolean
}) => {
  const uniqueMessages = [
    ...new Set(violations.map(v => ROUTE_VIOLATION_LABELS[v.type] ?? v.type)),
  ]

  return (
    <div css={{ display: 'flex', width: '100%', alignItems: 'stretch' }}>
      {/* Left rail — owns top + bottom connectors so both flex:1 symmetrically */}
      <div
        css={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 44,
        }}
      >
        {/* Top connector (waypoint → violation) — flex:1 so it matches message height */}
        <div
          css={{
            position: 'relative',
            flex: 1,
            minHeight: ROW_GAP,
            width: '100%',
          }}
        >
          <div
            css={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 2,
              background: `repeating-linear-gradient(to bottom, ${ERROR_COLOR} 0px, ${ERROR_COLOR} 5px, transparent 5px, transparent 10px)`,
            }}
          />
        </div>

        {/* Icon — white circle matching the map violation marker */}
        <div
          css={{
            position: 'relative',
            zIndex: 1,
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill={ERROR_COLOR}
            />
          </svg>
        </div>

        {/* Bottom connector (violation → next waypoint) */}
        {hasNextWaypoint && (
          <div
            css={{
              position: 'relative',
              flex: 1,
              minHeight: ROW_GAP,
              width: '100%',
            }}
          >
            <div
              css={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2,
                background: `repeating-linear-gradient(to bottom, ${ERROR_COLOR} 0px, ${ERROR_COLOR} 5px, transparent 5px, transparent 10px)`,
              }}
            />
          </div>
        )}
      </div>

      {/* Message column — aligned with the icon via paddingTop/paddingBottom */}
      <div
        css={{
          flex: 1,
          minWidth: 0,
          paddingLeft: 20,
          paddingRight: DRAG_HANDLE_WIDTH,
          paddingTop: ROW_GAP,
          paddingBottom: hasNextWaypoint ? ROW_GAP : 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          css={{
            width: '100%',
            padding: '6px 10px',
            backgroundColor: '#ffebee',
            borderLeft: `3px solid ${ERROR_COLOR}`,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {uniqueMessages.map(msg => (
            <Text
              key={msg}
              size="sm"
              css={{ color: ERROR_COLOR, lineHeight: 1.4 }}
            >
              MOIA: {msg}
            </Text>
          ))}
        </div>
      </div>
    </div>
  )
}

// Keeps the last non-null violations in a ref so the banner content doesn't
// blank out before the height-collapse exit animation finishes.
// The AnimatedCollapse stays mounted as long as `displayed` is non-null —
// no unmount/remount cycle, which avoids the "nothing appears on re-entry" bug.
const ViolationSlot = ({
  violations,
  hasNextWaypoint,
  dragging,
}: {
  violations: RouteViolation[] | null
  hasNextWaypoint: boolean
  dragging: boolean
}) => {
  const lastViolationsRef = useRef(violations)
  if (violations) lastViolationsRef.current = violations
  const displayed = lastViolationsRef.current

  const collapsed = !violations || dragging

  if (!displayed) return null

  return (
    <AnimatedCollapse collapsed={collapsed}>
      <ViolationBanner
        violations={displayed}
        hasNextWaypoint={hasNextWaypoint}
      />
    </AnimatedCollapse>
  )
}

// ── WaypointList ───────────────────────────────────────────────────────────

export const WaypointList = ({
  waypoints,
  onChange,
  onClearHeading,
  onReorderStart,
  violations = [],
  headings = {},
}: WaypointListProps) => {
  const [isDraggingAny, setIsDraggingAny] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  // Capture pixel width at drag-start — the DragOverlay clone is portalled to
  // document.body where width:100% would mean viewport width.
  const [overlayWidth, setOverlayWidth] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const sensors = useSensors(
    // 5px activation distance distinguishes intentional drag from scroll flick
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const violationsByLeg = useMemo(() => {
    const map = new Map<number, RouteViolation[]>()
    for (const v of violations) {
      if (v.legIndex == null) continue
      const existing = map.get(v.legIndex) ?? []
      existing.push(v)
      map.set(v.legIndex, existing)
    }
    return map
  }, [violations])

  const updateWaypoint = useCallback(
    (index: number, value: string) => {
      const updated = [...waypoints]
      const coords = parseCoordinates(value)
      const headingPatch =
        coords?.heading !== undefined ? { heading: coords.heading } : {}
      updated[index] = {
        ...updated[index],
        address: value,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        ...headingPatch,
      }
      onChange(updated)
    },
    [waypoints, onChange],
  )

  const removeWaypoint = useCallback(
    (index: number) => onChange(waypoints.filter((_, i) => i !== index)),
    [waypoints, onChange],
  )

  const clearWaypoint = useCallback(
    (index: number) => {
      const wp = waypoints[index]
      const updated = [...waypoints]
      updated[index] = {
        ...wp,
        lat: null,
        lng: null,
        address: '',
        validated: false,
        desiredLat: null,
        desiredLng: null,
      }
      onChange(updated)
      onClearHeading?.(wp.id)
    },
    [waypoints, onChange, onClearHeading],
  )

  const handlePasteOnFirst = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const lines = e.clipboardData
        .getData('text')
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean)
      if (lines.length <= 1) return
      const parsed: Waypoint[] = []
      for (const line of lines) {
        const coords = parseCoordinates(line)
        if (!coords) continue
        parsed.push({
          ...createWaypoint(),
          lat: coords.lat,
          lng: coords.lng,
          heading: coords.heading ?? null,
          address: [
            coords.lat.toFixed(6),
            coords.lng.toFixed(6),
            ...(coords.heading !== undefined ? [String(coords.heading)] : []),
          ].join(', '),
        })
      }
      if (parsed.length < 2) return
      e.preventDefault()
      onChange(parsed)
    },
    [onChange],
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(String(event.active.id))
      setIsDraggingAny(true)
      if (listRef.current) setOverlayWidth(listRef.current.offsetWidth)
      onReorderStart?.()
    },
    [onReorderStart],
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setIsDraggingAny(false)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null)
      setIsDraggingAny(false)
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = waypoints.findIndex(w => w.id === active.id)
      const newIndex = waypoints.findIndex(w => w.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = [...waypoints]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)
      onChange(reordered)
    },
    [waypoints, onChange],
  )

  const activeWaypoint = waypoints.find(w => w.id === activeId)
  const activeIndex = waypoints.findIndex(w => w.id === activeId)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={waypoints.map(w => w.id)}
        strategy={verticalListSortingStrategy}
      >
        {/* 5px left padding gives left-pointing heading arrows room so they're
            not clipped at the panel edge. The input column is flex:1, so it
            absorbs the 5px (right edge and rail→input gap stay fixed). */}
        <div
          ref={listRef}
          css={{ display: 'flex', flexDirection: 'column', paddingLeft: 5 }}
        >
          {waypoints.map((wp, idx) => {
            const legViolations = violationsByLeg.get(idx)
            const isLast = idx === waypoints.length - 1
            return (
              <SortableWaypoint
                key={wp.id}
                waypoint={wp}
                index={idx}
                showConnector={!isLast && !isDraggingAny}
                dragging={isDraggingAny}
                nextLegHasViolation={violationsByLeg.has(idx)}
                canRemove={waypoints.length > 2}
                heading={headings[wp.id]}
                onUpdate={updateWaypoint}
                onRemove={removeWaypoint}
                onClear={clearWaypoint}
                onPaste={idx === 0 ? handlePasteOnFirst : undefined}
              >
                <ViolationSlot
                  violations={legViolations ?? null}
                  hasNextWaypoint={!isLast}
                  dragging={isDraggingAny}
                />
              </SortableWaypoint>
            )
          })}
        </div>
      </SortableContext>

      {/* DragOverlay is portalled to document.body so its position:fixed is not
          affected by the floating panel's CSS transform containing block. It stays
          inside DndContext in the React tree and has full access to drag context.
          zIndex must exceed the AssignTripModal backdrop (10001) — that modal is
          also portalled to document.body, so a lower overlay renders behind it and
          the dragged row appears to vanish. */}
      {createPortal(
        <DragOverlay
          dropAnimation={{
            duration: 200,
            easing: 'cubic-bezier(0.2, 0, 0, 1)',
          }}
          style={{ zIndex: 10002 }}
        >
          {activeWaypoint && (
            <div style={{ width: overlayWidth }} css={{ borderRadius: 8 }}>
              <WaypointRow
                waypoint={activeWaypoint}
                index={activeIndex}
                showConnector={false}
                canRemove={waypoints.length > 2}
                heading={headings[activeWaypoint.id]}
                onUpdate={() => {}}
                onRemove={() => {}}
                onClear={() => {}}
                isOverlay
              />
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}
